import { inject, Injectable } from '@angular/core';
import { EMPTY_USER, IUser, User } from '@core/models/user.model';
import { AppStore } from './store/app.store';

/**
 * Servizio repository per la gestione dell'utente.
 * Si occupa di recuperare, salvare e aggiornare i dati utente nello store dell'applicazione.
 *
 * Utilizza AppStore per la gestione dello stato dell'applicazione.
 *
 * @example
 * // Recupera l'utente corrente
 * const user = userRepository.getUser();
 *
 * // Salva un nuovo utente
 * userRepository.saveUser(user);
 */
@Injectable({
  providedIn: 'root'
})
export class UserRepository {
  private readonly appStore = inject(AppStore);

  /**
   * Recupera l'utente dallo store dell'applicazione.
   * Restituisce EMPTY_USER se l'utente non è presente nello store.
   * @returns L'utente recuperato dallo store o EMPTY_USER se non presente.
   */
  getUser(): User {
    const userData = this.appStore.user();
    if (!userData) {
      return new User(EMPTY_USER);
    }
    // Se è già un'istanza di User, ritorna direttamente
    if (userData instanceof User) {
      return userData;
    }
    // Altrimenti crea una nuova istanza di User
    return new User(userData);
  }

  /**
   * Salva l'utente nello store dell'applicazione.
   * @param user - L'utente da salvare nello store.
   */
  saveUser(user: User): void {
    // Converte User in IUser per lo store
    const userData: IUser = user instanceof User ? user.toJSON() : user;
    this.appStore.setUser(userData);
  }

  /**
   * Aggiorna l'utente corrente nello store.
   * @param user - Istanza dell'utente da aggiornare nello store.
   */
  updateCurrentUser(user: User): void {
    this.saveUser(user);
  }
}
